import type { QueryObserverResult } from "@tanstack/react-query";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	useCallback,
	useContext,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { z } from "zod";

import { routes } from "~/app/routes/web-routes.ts";
import {
	searchAddressBookItems,
	useAddressBookItems,
} from "~/features/address-book/hooks/use-address-book.ts";
import { useSavedCommodities } from "~/features/commodities/hooks/use-commodities.ts";
import { QuoteDetailsContext } from "~/features/quote/context/quote-details-context.tsx";
import { useAddressBookAccessorials } from "~/features/quote/hooks/use-address-book-accessorials.ts";
import { formatQuoteState } from "~/features/quote/utils/format-quote-state.ts";
import type { LocationValue } from "~/features/quote/utils/quote-location-utils.ts";
import {
	findExactMatchingAddressBookItem,
	mapStopDetailsToAddressBookItem,
	verifyStopAddressBookId,
} from "~/features/quote/utils/quote-location-utils.ts";
import { useTemplates } from "~/features/templates/hooks/use-templates.ts";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useFeatureFlagGate } from "~/shared/hooks/use-feature-flag-gate.ts";
import { useUserConfiguration } from "~/shared/hooks/use-user-configuration.ts";
import type { Accessorial } from "~/shared/schemas/accessorial-schema.ts";
import { accessorialSchema } from "~/shared/schemas/accessorial-schema.ts";
import type {
	AddressBookCreateBody,
	AddressBookItemSummary,
	AddressBookUpdateBodyV2,
} from "~/shared/schemas/address-book-schema.ts";
import { addressBookUpdateResponseV2Schema } from "~/shared/schemas/address-book-schema.ts";
import type { CarrierPrice } from "~/shared/schemas/carrier-schema.ts";
import type { Commodity } from "~/shared/schemas/commodity-schema.ts";
import type { Config } from "~/shared/schemas/config-schema.ts";
import { ConfigurationType } from "~/shared/schemas/config-schema.ts";
import type {
	CreditLimit,
	CustomerDetails,
	PrimaryBroker,
} from "~/shared/schemas/customer-schema";
import {
	creditLimitSchema,
	customerDetailsSchema,
	primaryBrokerSchema,
} from "~/shared/schemas/customer-schema.ts";
import { freightClassSchema } from "~/shared/schemas/freight-classes-schema.ts";
import {
	hazmatClassSchema,
	hazmatGroupSchema,
} from "~/shared/schemas/hazmat-schema.ts";
import type { Holiday } from "~/shared/schemas/holiday-schema.ts";
import { holidaySchema } from "~/shared/schemas/holiday-schema.ts";
import type { Country } from "~/shared/schemas/location-state-schema.ts";
import { countrySchema } from "~/shared/schemas/location-state-schema.ts";
import type {
	QuoteBody,
	QuoteResponse,
} from "~/shared/schemas/quote-schema.ts";
import {
	quoteBodySchema,
	quoteResponseSchema,
} from "~/shared/schemas/quote-schema.ts";
import type {
	QuoteRecall,
	ShipmentCustomer,
} from "~/shared/schemas/shipment-management-schema.ts";
import type { TemplateGridItem } from "~/shared/schemas/template-schema.ts";
import { unitTypeSchema } from "~/shared/schemas/unit-type-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import {
	deleteAndValidate,
	getAndValidate,
	postAndValidate,
	putAndValidate,
} from "~/shared/services/api/fetch-client.ts";
import type { QuoteDetailsPageLoadData } from "~/shared/types/hooks-types";
import type {
	OnChange,
	QuoteStateType,
} from "~/shared/types/quote-details-types.ts";
import { base64Decode } from "~/shared/utils/base64-utils.ts";
import {
	ddCreateQuote,
	ddUseDefAddress,
	toDdWorkflow,
} from "~/shared/utils/datadog-actions.ts";
import { sendFinishCreateQuoteEvent } from "~/shared/utils/google-analytics-helper.ts";
import { idGenerator } from "~/shared/utils/id-generator.ts";
import { scrollMainContentToTop } from "~/shared/utils/scroll-to-top.ts";
import {
	formatDateToLocalISOString,
	toCamelCase,
} from "~/shared/utils/string-fns.ts";

import { initVal } from "../components/quote-details/constants/quote-detail-constants.ts";
import { useQuoteAddressContext } from "../context/quote-address-context.tsx";
import { convertCommodityToHandlingUnit } from "../utils/commodity-to-handling-unit.ts";
import {
	createLtlMutationErrorHandler,
	getLtlFallbackMessage,
} from "../utils/ltl-error-handler.ts";
import { mapPostQuoteCommoditiesToHandlingUnits } from "../utils/map-post-quote-commodity-to-handling-unit.ts";
import { mapQuoteBodyToQuoteState } from "../utils/map-quote-body-to-quote-state.ts";
import { mapQuoteRecallToQuoteBody } from "../utils/map-quote-recall-to-quote-body.ts";

const getStopTypeKey = (acc: Accessorial): string => {
	if (acc.stopType?.name) {
		return acc.stopType.name.toLowerCase() === "pick" ? "pickup" : "delivery";
	}
	return "addDetails";
};

export const sortAndFormatAccessorials = (accArr: Accessorial[]) => {
	const grouped: {
		selectable: Record<string, Accessorial[]>;
		autoApply: Record<string, Accessorial[]>;
		locationType: Record<string, Accessorial[]>;
	} = {
		selectable: { pickup: [], delivery: [] },
		autoApply: {},
		locationType: {},
	};

	for (const acc of accArr) {
		const usageTypeName = acc.accessorialUsageType?.name ?? "unknown";
		const key = toCamelCase(usageTypeName);
		const stopTypeKey = getStopTypeKey(acc);

		if (key in grouped) {
			const group = grouped[key as keyof typeof grouped];
			group[stopTypeKey] ??= [];
			group[stopTypeKey].push(acc);
		}
	}
	return grouped;
};

export const useQuoteDetailsPageLoadQueries =
	(): QueryObserverResult<QuoteDetailsPageLoadData> => {
		return useQuery<QuoteDetailsPageLoadData>({
			queryKey: ["quoteDetailsPageLoad"],
			queryFn: async (): Promise<QuoteDetailsPageLoadData> => {
				const [
					freightClassResult,
					accessorialsResult,
					unitTypesResult,
					hazmatClassesResult,
					hazmatGroupsResult,
					noFutureHolidaysResult,
					countriesResult,
				] = await Promise.all([
					getAndValidate(apiRoutes.FREIGHTCLASSES, freightClassSchema.array()),
					getAndValidate(apiRoutes.ACCESSORIALS, accessorialSchema.array()),
					getAndValidate(apiRoutes.UNITTYPES, unitTypeSchema.array()),
					getAndValidate(apiRoutes.HAZMATCLASSES, hazmatClassSchema.array()),
					getAndValidate(apiRoutes.HAZMATGROUPS, hazmatGroupSchema.array()),
					getAndValidate(`${apiRoutes.HOLIDAYS}false`, holidaySchema.array()),
					getAndValidate(apiRoutes.COUNTRIES, countrySchema.array()),
				]);

				return {
					freightClassData: freightClassResult.success
						? freightClassResult.data
						: [],
					accessorials: accessorialsResult.success
						? sortAndFormatAccessorials(accessorialsResult.data)
						: { selectable: {}, autoApply: {}, locationType: {} },
					unitTypes: unitTypesResult.success ? unitTypesResult.data : [],
					hazmatClasses: hazmatClassesResult.success
						? hazmatClassesResult.data
						: [],
					hazmatGroups: hazmatGroupsResult.success
						? hazmatGroupsResult.data
						: [],
					holidays: noFutureHolidaysResult.success
						? noFutureHolidaysResult.data
						: [],
					countries: countriesResult.success
						? countriesResult.data.map((c) => ({ id: idGenerator(), ...c }))
						: [],
				};
			},
			staleTime: 1000 * 60 * 60 * 24 * 2, // 2 days
			gcTime: 1000 * 60 * 60 * 24 * 3, // 3 days
			retry: 5,
			refetchOnWindowFocus: false,
			refetchOnMount: true,
			refetchOnReconnect: false,
		});
	};

export const useFilteredHolidays = (
	holidays: Holiday[] | undefined,
	pickupCountry: Country | string | undefined,
): Holiday[] => {
	return useMemo(() => {
		if (!holidays) return [];

		const countryAbbr = (
			typeof pickupCountry === "string"
				? pickupCountry
				: pickupCountry?.abbreviation
		)
			?.toString()
			.toUpperCase()
			.trim();

		if (!countryAbbr) {
			return holidays.filter((h) => h.isTQL);
		}

		// Handle both 2-letter and 3-letter country codes
		const isUSA = countryAbbr === "US" || countryAbbr === "USA";
		const isCAN = countryAbbr === "CA" || countryAbbr === "CAN";

		return holidays.filter((holiday) => {
			// TQL holidays apply to all quotes regardless of country
			if (holiday.isTQL) return true;

			if (isUSA && holiday.isUSA) return true;
			return !!(isCAN && holiday.isCanadian);
		});
	}, [holidays, pickupCountry]);
};

export const useUpdateAddressBookItem = () => {
	const queryClient = useQueryClient();
	const { isInternal } = useDeploymentType();
	return useMutation({
		mutationFn: async (body: AddressBookUpdateBodyV2) => {
			const baseRoute = isInternal
				? apiRoutes.UPDATEADDRESSBOOKITEMV2
				: apiRoutes.EXTERNALUPDATEADDRESSBOOKITEMV2;
			return await putAndValidate(
				baseRoute.replace(":addressBookItemId", String(body.addressBookItemId)),
				body,
				addressBookUpdateResponseV2Schema,
			);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["addressBookItems"] });
			queryClient.invalidateQueries({ queryKey: ["addressBookItemsFiltered"] });
		},
	});
};

export const useDeleteAddressBookItem = () => {
	const queryClient = useQueryClient();
	const { isInternal } = useDeploymentType();
	return useMutation({
		mutationFn: async (addressBookItemId: number) => {
			const baseRoute = isInternal
				? apiRoutes.DELETEADDRESSBOOKITEMV2
				: apiRoutes.EXTERNALDELETEADDRESSBOOKITEMV2;
			return await deleteAndValidate(
				baseRoute.replace(":addressBookItemId", String(addressBookItemId)),
				z.object({}).passthrough(),
			);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["addressBookItems"] });
			queryClient.invalidateQueries({ queryKey: ["addressBookItemsFiltered"] });
		},
	});
};

export const useCreateAddressBookItem = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (body: AddressBookCreateBody) => {
			return await postAndValidate(
				apiRoutes.CREATEADDRESSBOOKITEM,
				body,
				z.unknown(),
			);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["addressBookItems"] });
			queryClient.invalidateQueries({ queryKey: ["addressBookItemsFiltered"] });
		},
	});
};

export const useCreateQuote = () => {
	const { isInternal } = useDeploymentType();
	const getUrl = useMemo(() => {
		return isInternal ? apiRoutes.QUOTE : apiRoutes.CUSTOMERQUOTE;
	}, [isInternal]);
	return useMutation({
		mutationFn: async (body: QuoteBody) => {
			return await postAndValidate(getUrl, body, quoteResponseSchema, {
				onError: createLtlMutationErrorHandler(
					getLtlFallbackMessage(isInternal),
				),
			});
		},
	});
};

export const useGetQuote = (
	quoteId: number,
	options?: { enabled?: boolean },
) => {
	const { isInternal } = useDeploymentType();

	return useQuery<QuoteResponse>({
		queryKey: ["getQuote", quoteId],
		queryFn: async (): Promise<QuoteResponse> => {
			const url = isInternal
				? `${apiRoutes.GETQUOTE}/${quoteId}`
				: `${apiRoutes.GETCUSTOMERQUOTE}/${quoteId}`;
			const result = await getAndValidate(url, quoteResponseSchema, {
				onError: createLtlMutationErrorHandler(
					getLtlFallbackMessage(isInternal),
				),
			});
			if (result.success) {
				return result.data as QuoteResponse;
			}
			throw new Error(result.error || "Failed to get quote");
		},
		enabled: options?.enabled ?? quoteId > 0, // Don't fetch if quoteId is 0 or less, unless explicitly enabled
	});
};

export const getSearchCustomerRoute = (searchTerm: string) => {
	return apiRoutes.CUSTOMERSEARCH.replace(
		":userInput",
		encodeURIComponent(searchTerm),
	);
};

export const useGetCustomerCredit = (customerId: number) => {
	const { isInternal } = useDeploymentType();
	const { setCreditDetails } = useContext(QuoteDetailsContext);
	const query = useQuery<CreditLimit>({
		queryKey: ["getCustomerCredit", customerId],
		queryFn: async (): Promise<CreditLimit> => {
			const result = await getAndValidate(
				`${apiRoutes.CREDITINFO}${customerId}`,
				creditLimitSchema,
			);
			if (result.success) {
				return result.data;
			}
			return {} as CreditLimit;
		},
		enabled: customerId > 0 && isInternal,
	});

	useEffect(() => {
		if (query.data && query.isSuccess) {
			setCreditDetails({
				customerCredits: query.data,
			});
		}
	}, [query.data, query.isSuccess, setCreditDetails]);

	useEffect(() => {
		if (query.isError) {
			console.error("Get customer credits failed:", query.error);
			setCreditDetails({
				customerCredits: {
					creditLimit: 0,
					availableCredit: 0,
					totalBalance: 0,
				},
			});
		}
	}, [query.isError, query.error, setCreditDetails]);

	return {
		creditInfo: query.data,
		isLoading: query.isLoading,
		error: query.error,
		isError: query.isError,
	};
};

// Pending backend follow-up: data alignment investigation
// `enabled` gates the request — account reps is an internal-only feature, so the
// external experience must not call this endpoint (the API returns 403).
export const useGetCustomerAr = (
	customerId: number,
	brokerId: number,
	enabled = true,
) => {
	const { setCustomerAr } = useContext(QuoteDetailsContext);
	const query = useQuery<PrimaryBroker>({
		queryKey: ["getCustomerAr", customerId, brokerId],
		queryFn: async (): Promise<PrimaryBroker> => {
			const result = await getAndValidate(
				apiRoutes.BROKER.replace(":customerId", String(customerId)).replace(
					":brokerId",
					String(brokerId),
				),
				// Backend signals "no AR on file" with an empty string or an empty body.
				z.union([primaryBrokerSchema, z.literal("")]).nullish(),
			);
			if (result.success && result.data && typeof result.data !== "string") {
				return result.data;
			}
			return {} as PrimaryBroker;
		},
		enabled: enabled && customerId > 0 && brokerId > 0,
	});

	useEffect(() => {
		if (query.data && query.isSuccess) {
			setCustomerAr(query.data);
		}
	}, [query.data, query.isSuccess, setCustomerAr]);

	useEffect(() => {
		if (query.isError) {
			console.error("Get customer AR:", query.error);
		}
	}, [query.isError, query.error]);

	return {
		arInfo: query.data,
		isLoading: query.isLoading,
		error: query.error,
		isError: query.isError,
	};
};

export const useGetCustomerDetails = (customerId: number) => {
	const { setCustomerDetails } = useContext(QuoteDetailsContext);
	const query = useQuery<CustomerDetails>({
		queryKey: ["getCustomerDetails", customerId],
		queryFn: async (): Promise<CustomerDetails> => {
			const result = await getAndValidate(
				apiRoutes.CUSTOMER.replace(":customerId", String(customerId)),
				customerDetailsSchema,
			);
			if (result.success) {
				return result.data;
			}
			return {} as CustomerDetails;
		},
		enabled: customerId > 0,
	});

	useEffect(() => {
		if (query.data && query.isSuccess) {
			setCustomerDetails(query.data);
		}
	}, [query.data, query.isSuccess, setCustomerDetails]);

	useEffect(() => {
		if (query.isError) {
			console.error("Get customer details:", query.error);
		}
	}, [query.isError, query.error]);

	return {
		customerDetails: query.data,
		isLoading: query.isLoading,
		error: query.error,
		isError: query.isError,
	};
};

export const useCustomerDetailsFetcher = (customerId?: number) => {
	const { setCustomerDetails } = useContext(QuoteDetailsContext);
	const { isInternal } = useDeploymentType();

	const fetchCustomerDetails = useCallback(
		async (id: number) => {
			if (!isInternal) return;
			const fullCustomerData = await getAndValidate(
				`${apiRoutes.SELECTCUSTOMER}${id}`,
				customerDetailsSchema,
			);

			if (fullCustomerData?.success) {
				setCustomerDetails(fullCustomerData.data);
			} else {
				console.error(
					"Failed to fetch customer details:",
					fullCustomerData?.error ?? fullCustomerData,
				);
			}
		},
		[setCustomerDetails, isInternal],
	);

	useEffect(() => {
		if (!customerId) return;
		void fetchCustomerDetails(customerId);
	}, [customerId, fetchCustomerDetails]);
};

export const useUserDefaultsFetcher = (customerId?: number, skip = false) => {
	const { quoteId } = useParams<{ quoteId: string }>();
	const { userConfigs, isLoadingConfigs } = useUserConfiguration();
	const { setSelectedAddressBookItem, resetSelectedAddressBookItems } =
		useQuoteAddressContext();
	const { applyAddressBookAccessorials, allAccessorials } =
		useAddressBookAccessorials();
	const {
		addItem,
		removeAllBlankItems,
		setQuoteState,
		quoteState: { measuringType, id: quoteStateId },
		workflow,
	} = useContext(QuoteDetailsContext);
	const lastCustomerId = useRef<number | undefined>(undefined);
	const skipDefaultTemplateRef = useRef(false);
	const { isInternal } = useDeploymentType();
	const { isEnabled: isTemplatesEnabled } = useFeatureFlagGate(
		"traxLtlTemplatesPage",
	);

	const { data: addressBookItems, isLoading: isLoadingAddressBook } =
		useAddressBookItems(
			{ customerId: customerId ?? 0 },
			!!customerId && !skip,
			isInternal,
		);

	const { data: savedItems, isLoading: isLoadingSavedItems } =
		useSavedCommodities(
			String(customerId ?? 0),
			!!customerId && !skip,
			isInternal,
		);

	// Fetch templates to check for a default template (configType 5)
	const templatesQuery = useTemplates(
		skip || !isTemplatesEnabled ? undefined : customerId,
	);
	const templates = templatesQuery.data;
	const isLoadingTemplates = templatesQuery.isLoading;

	// Page data needed for template hydration (freight classes, unit types)
	const { data: pageData } = useQuoteDetailsPageLoadQueries();

	const applyAddressDefaults = useCallback(
		(configs: Config[], items: AddressBookItemSummary[]) => {
			const findConfig = (type: ConfigurationType) =>
				configs.find(
					(c) =>
						c.configType === type &&
						Number(c.customerId) === Number(customerId),
				);

			const pickupDefault = findConfig(ConfigurationType.PickDefaultAddress);
			const deliveryDefault = findConfig(ConfigurationType.DropDefaultAddress);

			if (pickupDefault) {
				const address = items.find(
					(item) =>
						String(item.addressBookItemId) === pickupDefault.configValue ||
						String(item.legacyAddressId) === pickupDefault.configValue,
				);
				if (address) {
					setSelectedAddressBookItem("pickup", address);
					applyAddressBookAccessorials("pickup", address);
					ddUseDefAddress(toDdWorkflow(workflow), "pickup");
				}
			}

			if (deliveryDefault) {
				const address = items.find(
					(item) =>
						String(item.addressBookItemId) === deliveryDefault.configValue ||
						String(item.legacyAddressId) === deliveryDefault.configValue,
				);
				if (address) {
					setSelectedAddressBookItem("delivery", address);
					applyAddressBookAccessorials("delivery", address);
					ddUseDefAddress(toDdWorkflow(workflow), "drop");
				}
			}
		},
		[
			applyAddressBookAccessorials,
			customerId,
			setSelectedAddressBookItem,
			workflow,
		],
	);

	const applyCommodityDefaults = useCallback(
		(configs: Config[], items: Commodity[]) => {
			const commodityConfigs = configs.filter(
				(c) =>
					c.configType === ConfigurationType.CommodityDefault &&
					Number(c.customerId) === Number(customerId),
			);

			let addedCount = 0;
			for (const config of commodityConfigs) {
				const commodity = items.find(
					(item) => String(item.id) === config.configValue,
				);
				if (commodity) {
					addItem(convertCommodityToHandlingUnit(commodity, measuringType));
					addedCount += 1;
				}
			}

			// Only strip the blank starter row once a default actually replaced it,
			// otherwise the quote is left with no handling unit rows at all.
			if (addedCount > 0) {
				removeAllBlankItems();
			}
		},
		[addItem, customerId, removeAllBlankItems, measuringType],
	);

	/**
	 * Apply a default template to the quote state.
	 * Returns true if the template was successfully applied.
	 */
	const applyDefaultTemplate = useCallback(
		(template: TemplateGridItem): boolean => {
			if (!pageData || !allAccessorials || allAccessorials.length === 0) {
				return false;
			}

			try {
				resetSelectedAddressBookItems();

				const data = JSON.parse(
					base64Decode(template.raw.templateJson),
				) as Partial<QuoteBody>;
				const { nextState, pickupItem, deliveryItem } =
					mapQuoteBodyToQuoteState(data, initVal, {
						accessorials: allAccessorials,
						freightClasses: pageData.freightClassData,
						unitTypes: pageData.unitTypes,
					});

				const verifiedPickup = verifyStopAddressBookId(
					pickupItem,
					data.pickupDetails,
					data.origin as LocationValue | undefined,
					"pickup",
					addressBookItems,
				);
				const verifiedDelivery = verifyStopAddressBookId(
					deliveryItem,
					data.deliveryDetails,
					data.destination as LocationValue | undefined,
					"delivery",
					addressBookItems,
				);
				if (verifiedPickup) {
					setSelectedAddressBookItem("pickup", verifiedPickup);
				}
				if (verifiedDelivery) {
					setSelectedAddressBookItem("delivery", verifiedDelivery);
				}

				setQuoteState((prev) => ({
					...nextState,
					shipmentDate: prev.shipmentDate,
					customer: prev.customer,
					customerDetails: prev.customerDetails,
					customerAr: prev.customerAr,
					customerCredits: prev.customerCredits,
					templateId: template.templateId,
					templateName: template.templateName,
					measuringType: prev.measuringType,
				}));

				return true;
			} catch (error) {
				console.error("Failed to apply default template:", error);
				return false;
			}
		},
		[
			pageData,
			allAccessorials,
			addressBookItems,
			resetSelectedAddressBookItems,
			setSelectedAddressBookItem,
			setQuoteState,
		],
	);

	/**
	 * Reset the tracking ref so individual defaults will be re-applied on the next
	 * render cycle. Used when the user clears an applied template so that address
	 * and commodity defaults can be restored.
	 */
	const reapplyDefaults = useCallback(() => {
		skipDefaultTemplateRef.current = true;
		lastCustomerId.current = undefined;
	}, []);

	/**
	 * Attempt to apply a default template (configType 5) if available and not skipped.
	 * Returns true if a template was found and successfully applied.
	 */
	const tryApplyDefaultTemplate = useCallback((): boolean => {
		if (skipDefaultTemplateRef.current || !isTemplatesEnabled || !templates) {
			return false;
		}

		const defaultTemplateConfig = userConfigs?.find(
			(c) =>
				c.configType === ConfigurationType.QuoteTemplateDefault &&
				Number(c.customerId) === Number(customerId),
		);

		if (defaultTemplateConfig) {
			const defaultTemplate = templates.find(
				(t) => String(t.templateId) === defaultTemplateConfig.configValue,
			);
			if (defaultTemplate) {
				return applyDefaultTemplate(defaultTemplate);
			}
		}

		return false;
	}, [
		isTemplatesEnabled,
		templates,
		userConfigs,
		customerId,
		applyDefaultTemplate,
	]);

	useEffect(() => {
		// Do not apply defaults if we are revising an existing quote
		if (quoteId) {
			return;
		}

		// Do not apply defaults if quoteState has not been reset yet.
		// When navigating from an existing quote (carrier-selection) to a new quote,
		// the context may still hold the previous quote's id until resetForm() propagates.
		// Applying defaults against stale state causes the previous quote's user defaults
		// to pre-populate the new quote form.
		if (quoteStateId) {
			return;
		}

		// Skip if explicitly disabled
		if (skip) {
			return;
		}

		if (!customerId || customerId === 0) {
			lastCustomerId.current = 0;
			return;
		}

		if (
			isLoadingConfigs ||
			isLoadingAddressBook ||
			isLoadingSavedItems ||
			isLoadingTemplates ||
			!addressBookItems ||
			!userConfigs ||
			!savedItems ||
			customerId === lastCustomerId.current
		) {
			return;
		}
		lastCustomerId.current = customerId;

		// Case 2: Check for a default template (configType 5) for this customer.
		// If set, auto-apply the template and skip individual defaults.
		// skipDefaultTemplateRef is true when the user explicitly cleared a template
		// and wants individual defaults to be restored instead.
		if (tryApplyDefaultTemplate()) {
			return; // Default template applied — skip individual defaults
		}
		skipDefaultTemplateRef.current = false;

		// Case 1: No default template — apply individual address/commodity defaults
		applyAddressDefaults(userConfigs, addressBookItems);
		applyCommodityDefaults(userConfigs, savedItems.savedCommodities);
	}, [
		customerId,
		quoteId,
		quoteStateId,
		skip,
		userConfigs,
		isLoadingConfigs,
		addressBookItems,
		isLoadingAddressBook,
		savedItems,
		isLoadingSavedItems,
		templates,
		isLoadingTemplates,
		tryApplyDefaultTemplate,
		applyAddressDefaults,
		applyCommodityDefaults,
	]);

	return { reapplyDefaults };
};

type SubmitQuoteOptions = {
	successRoute?: string;
	shouldNavigate?: boolean;
};

type SubmitQuoteResult =
	| { success: true; data: QuoteResponse }
	| { success: false; error: string };

export const useSubmitQuote = (options?: SubmitQuoteOptions) => {
	const { quoteState, mergeResponseIntoQuoteState, setQuoteTotals, workflow } =
		useContext(QuoteDetailsContext);
	const { selectedAddressBookItems } = useQuoteAddressContext();
	const navigate = useNavigate();
	const { mutateAsync, isPending } = useCreateQuote();
	const { isInternal } = useDeploymentType();

	const submitQuote = async (): Promise<SubmitQuoteResult> => {
		const body = formatQuoteState(
			quoteState,
			selectedAddressBookItems,
			true,
			isInternal,
		);
		const parsedJson = quoteBodySchema.safeParse(body);

		if (parsedJson.success) {
			const result = await mutateAsync(parsedJson.data);
			if (result.success) {
				const handlingUnits = mapPostQuoteCommoditiesToHandlingUnits(
					result.data.quoteCommodities,
				);
				// need to set this returned quote data somewhere
				mergeResponseIntoQuoteState({
					id: result.data.id,
					carrierPrices: result.data.carrierPrices as CarrierPrice[],
					items: handlingUnits,
					eTag: result.data.eTag,
					createdDate: result.data.createdDate,
					expirationDate: result.data.expirationDate,
					publicGuid: result.data.publicGuid,
					// set insurance values
					insuranceApplyType: result.data.insuranceApplyTypeId,
					insuranceApplyTypeId: result.data.insuranceApplyTypeId,
					insuranceProductType: null,
					insuranceProductTypeId: result.data.insuranceProductTypeId,
					insuranceValue: result.data.insuranceValue,
					hasRoutingGuideMatch: result.data.hasRoutingGuideMatch,
					selectedCarrierPriceId: result.data.selectedCarrierPriceId,
				});
				setQuoteTotals();
				sendFinishCreateQuoteEvent(result.data.id.toString(), workflow);
				ddCreateQuote(result.data.id.toString(), toDdWorkflow(workflow));
				// Navigation will handle step transition automatically.
				// If a successRoute is specified, use it; otherwise, if a carrier
				// was already selected, skip ahead to shipment details.
				if (options?.shouldNavigate ?? true) {
					const newQuoteId = result.data.id.toString();
					const defaultRoute =
						result.data.selectedCarrierPriceId == null
							? routes.carrierSelection
							: routes.shipmentDetails;
					navigate(
						(options?.successRoute ?? defaultRoute).replace(
							":quoteId",
							newQuoteId,
						),
						{ replace: true, state: { fromCreate: true } },
					);
					// Reset scroll to the top of the page so the carrier selection /
					// shipment details page starts at the top instead of inheriting the
					// previous page's scroll position.
					scrollMainContentToTop();
				}
				return { success: true, data: result.data };
			} else {
				console.error("Error creating quote:", result.error);
				const errorResult: SubmitQuoteResult = {
					success: false,
					error: result.error || "Failed to create quote",
				};
				console.error("Returning error result:", errorResult);
				return errorResult;
			}
		} else {
			console.error("Error parsing customer data:", parsedJson.error);
			return { success: false, error: "Invalid quote data" };
		}
	};

	return { submitQuote, isLoading: isPending };
};

// Enhanced Quote Loader that can be used across different pages
export const useQuoteLoader = (options?: {
	includeCustomerData?: boolean;
	includeFormReset?: boolean;
	skipQuoteFetch?: boolean;
}) => {
	const { quoteId } = useParams<{ quoteId: string }>();
	const location = useLocation() as {
		state?: {
			isRerate?: boolean;
			isCopy?: boolean;
			isCopyQuote?: boolean;
			quoteRecall?: QuoteRecall;
			copyQuoteCustomer?: ShipmentCustomer;
		};
		pathname: string;
		search: string;
		key: string;
	};
	const navigate = useNavigate();

	const {
		quoteState,
		loadQuoteFromResponse,
		resetForm,
		setCustomerDetails,
		setCreditDetails,
		setQuoteState,
	} = useContext(QuoteDetailsContext);
	const { setSelectedAddressBookItem } = useQuoteAddressContext();
	const { data: pageLoadData } = useQuoteDetailsPageLoadQueries();
	const { allAccessorials } = useAddressBookAccessorials();
	const queryClient = useQueryClient();
	const { isInternal } = useDeploymentType();
	const loadedQuoteId = useRef<number | null>(
		quoteId && quoteId === String(quoteState.id) ? quoteState.id : null,
	);
	const resolvedAddressBookQuoteId = useRef<number | null>(null);
	// Track whether navigation came from the create-quote flow so we
	// don't incorrectly mark the workflow as "resume_quote" after the
	// QuoteDetailsProvider remounts due to a key change.
	const fromCreate = (location.state as { fromCreate?: boolean } | null)
		?.fromCreate;
	// Copy Quote / Copy Shipment navigate with this flag so we skip the saved
	// address book re-resolution and keep copied stops as typed (detached).
	const isCopy = location.state?.isCopy ?? false;
	// Copy Quote / Copy Shipment payload used to populate a fresh quote/shipment.
	const quoteRecall = location.state?.quoteRecall;
	const copyQuoteCustomer = location.state?.copyQuoteCustomer;
	// Default options
	const { includeCustomerData = true, includeFormReset = true } = options ?? {};

	// Reset form immediately when there's no quoteId (creating new quote)
	// Use useLayoutEffect to ensure this runs synchronously before any other effects.
	// Skipped for a Copy Quote/Shipment navigation: the quoteRecall effect below
	// performs its own clean populate, and resetting here would wipe it — under
	// React StrictMode's double-invoked mount effects this otherwise leaves the
	// form empty on a repeat copy.
	useLayoutEffect(() => {
		if (!quoteId && includeFormReset && resetForm && !quoteRecall) {
			resetForm();
		}
	}, [quoteId, resetForm, includeFormReset, quoteRecall]);

	// Only fetch quote if we have a quoteId in URL and it's different from current state
	// AND it's different from the quote we already loaded for this URL
	const shouldLoadQuote = Boolean(
		!options?.skipQuoteFetch &&
		quoteId &&
		quoteId !== String(quoteState.id) &&
		quoteId !== String(loadedQuoteId.current),
	);
	const {
		data: existingQuote,
		isLoading: isLoadingQuote,
		isFetched: quoteIsFetched,
	} = useGetQuote(shouldLoadQuote ? Number(quoteId) : 0, {
		enabled: shouldLoadQuote,
	});

	// Populate quote state from URL parameter when existing quote is loaded
	const existingQuoteId = existingQuote?.id;
	useEffect(() => {
		if (
			existingQuote &&
			existingQuoteId &&
			existingQuoteId !== loadedQuoteId.current
		) {
			loadQuoteFromResponse(
				existingQuote,
				pageLoadData?.hazmatClasses,
				pageLoadData?.hazmatGroups,
				!fromCreate,
			);
			loadedQuoteId.current = existingQuoteId;

			// Populate selectedAddressBookItems from pickupDetails and deliveryDetails.
			// Force addressBookItemId to 0: the backend can echo back a
			// loosely-matched id (e.g. matched on postal code + street even when the
			// stop name was edited after applying a saved address), and trusting it
			// would re-show an edited stop as a saved address on reload. The id is
			// reassigned below only when the name-strict resolution finds an exact
			// match, so an edited stop stays detached (id 0).
			const pickupItem = mapStopDetailsToAddressBookItem(
				existingQuote.pickupDetails,
				"pickup",
				existingQuote.origin,
			);
			const deliveryItem = mapStopDetailsToAddressBookItem(
				existingQuote.deliveryDetails,
				"delivery",
				existingQuote.destination,
			);

			if (pickupItem) {
				setSelectedAddressBookItem(
					"pickup",
					{ ...pickupItem, addressBookItemId: 0 },
					"auto",
				);
			}
			if (deliveryItem) {
				setSelectedAddressBookItem(
					"delivery",
					{ ...deliveryItem, addressBookItemId: 0 },
					"auto",
				);
			}

			// Resolve real address book items via the v2 search endpoint.
			// The backend stores raw address fields on the quote (no addressBookItemId
			// or special instructions/accessorials), so we conservatively infer a
			// saved address only when both its location and applicable stop detail
			// exactly match the quote snapshot. Otherwise the synthetic item above
			// remains detached and authoritative.
			// Uses a separate ref from loadedQuoteId because the async resolution
			// must not re-trigger when the effect re-fires for other dep changes.
			// Silent fallback (keeps the synthetic item) on miss/error.
			//
			// Skipped for the copy flow: the backend can't tell us whether a stop
			// actually used a saved address (it only echoes raw fields), so a copied
			// stop whose typed values happen to equal a saved entry would be wrongly
			// re-shown as a saved address. On copy we keep the stops detached
			// (addressBookItemId 0) — the fields are still populated for editing.
			if (!isCopy && resolvedAddressBookQuoteId.current !== existingQuoteId) {
				resolvedAddressBookQuoteId.current = existingQuoteId;
				const customerId = existingQuote.customer?.id;

				// Internal API requires a customerId in the URL
				if (isInternal && !customerId) return;

				const resolveSide = async (
					side: "pickup" | "delivery",
					stopDetails: typeof existingQuote.pickupDetails,
					location: typeof existingQuote.origin,
				) => {
					const address1 = stopDetails?.address1?.trim();
					const postalCode = location?.postalCode?.trim();
					if (!address1 || !postalCode) return;

					try {
						const items = await queryClient.fetchQuery({
							queryKey: [
								"addressBookSearch",
								postalCode,
								customerId,
								isInternal,
							],
							queryFn: () =>
								searchAddressBookItems({
									searchTerm: postalCode,
									customerId,
									isInternal,
								}),
							staleTime: 1000 * 60 * 5,
						});
						// Guard: only apply if this is still the active quote
						if (resolvedAddressBookQuoteId.current !== existingQuoteId) {
							return;
						}
						const match = findExactMatchingAddressBookItem(
							items,
							stopDetails,
							location,
							side,
						);
						if (match) {
							setSelectedAddressBookItem(side, match, "auto");
						}
					} catch (error) {
						console.error(
							`Failed to resolve ${side} address book item from search:`,
							error,
						);
					}
				};

				void resolveSide(
					"pickup",
					existingQuote.pickupDetails,
					existingQuote.origin,
				);
				void resolveSide(
					"delivery",
					existingQuote.deliveryDetails,
					existingQuote.destination,
				);
			}

			// Clear shipment-specific fields so they don't carry into the new quote.
			// Applies to a rerate and to Copy Quote (which starts a fresh quote that
			// must not stay tied to the original shipment's PO/references).
			if (location.state?.isRerate || location.state?.isCopyQuote) {
				setQuoteState((prev) => ({
					...prev,
					customerReference: "",
					poNumber: null,
					poNumbers: "",
					soNumber: "",
					specialInstructions: "",
				}));
				navigate(`${location.pathname}${location.search}`, {
					replace: true,
					state: {},
				});
			}
		}
	}, [
		existingQuote,
		existingQuoteId,
		fromCreate,
		loadQuoteFromResponse,
		setSelectedAddressBookItem,
		pageLoadData?.hazmatClasses,
		pageLoadData?.hazmatGroups,
		location.state?.isRerate,
		location.state?.isCopyQuote,
		setQuoteState,
		navigate,
		location.pathname,
		location.search,
		queryClient,
		isInternal,
		isCopy,
	]);

	// Copy Quote populates a fresh quote from the shipment's quoteRecall payload
	// (navigated to /quote-details with no quoteId in the URL). Runs once the
	// reference data needed to resolve accessorials/commodities is available.
	// Keyed on the navigation's location.key (not a boolean) so a fresh Copy
	// Quote/Shipment navigation re-applies even when the page component is reused
	// rather than remounted (e.g. after back navigation then copying again).
	const appliedRecallKeyRef = useRef<string | null>(null);
	useEffect(() => {
		if (quoteId || !quoteRecall || appliedRecallKeyRef.current === location.key)
			return;
		if (!pageLoadData || !allAccessorials) return;
		appliedRecallKeyRef.current = location.key;

		const body = mapQuoteRecallToQuoteBody(quoteRecall);

		// A copied quote is created now, so a shipment date in the past is bumped
		// to today (mirrors loadQuoteFromResponse's normalization).
		const today = new Date();
		today.setHours(0, 0, 0, 0);
		const recalledDate = quoteRecall.shipmentDate
			? new Date(quoteRecall.shipmentDate)
			: today;
		recalledDate.setHours(0, 0, 0, 0);
		const normalizedShipmentDate =
			recalledDate < today
				? new Date().toISOString()
				: (quoteRecall.shipmentDate ?? new Date().toISOString());

		// The quoteRecall customerId can be missing, so fall back to the
		// shipment's customer. Seed a display name so the Customer Select shows
		// immediately; useGetCustomerDetails then loads the full record.
		const resolvedCustomerId =
			quoteRecall.customerId ?? copyQuoteCustomer?.id ?? 0;
		const resolvedCustomerName = copyQuoteCustomer?.customerName ?? "";

		setQuoteState((prev) => {
			// Map from a clean initial state (not prev) so a reused page component
			// doesn't retain fields from a previously copied quote/shipment.
			const { nextState } = mapQuoteBodyToQuoteState(body, initVal, {
				accessorials: allAccessorials,
				freightClasses: pageLoadData.freightClassData,
				unitTypes: pageLoadData.unitTypes,
			});
			return {
				...nextState,
				// Preserve provider-level bits that must survive the copy populate.
				measuringType: prev.measuringType,
				// Fresh quote: drop any id and shipment-specific references so the
				// copy is not tied to the original shipment.
				id: 0,
				customerId: resolvedCustomerId,
				customer: {
					...nextState.customer,
					id: resolvedCustomerId,
					name: resolvedCustomerName,
					nameWithId: resolvedCustomerName,
				},
				shipmentDate: normalizedShipmentDate,
				billingCustomerId: quoteRecall.billingCustomerId ?? null,
				customerReference: "",
				poNumber: null,
				poNumbers: "",
				soNumber: "",
			};
		});

		// Fill the pickup/delivery address fields directly from the copied stop
		// details. Kept detached (addressBookItemId 0) — no address book lookup.
		const pickupItem = mapStopDetailsToAddressBookItem(
			quoteRecall.pickupDetails,
			"pickup",
			body.origin as LocationValue | undefined,
		);
		const deliveryItem = mapStopDetailsToAddressBookItem(
			quoteRecall.deliveryDetails,
			"delivery",
			body.destination as LocationValue | undefined,
		);
		if (pickupItem) {
			setSelectedAddressBookItem(
				"pickup",
				{ ...pickupItem, addressBookItemId: 0 },
				"auto",
			);
		}
		if (deliveryItem) {
			setSelectedAddressBookItem(
				"delivery",
				{ ...deliveryItem, addressBookItemId: 0 },
				"auto",
			);
		}

		// Copy Quote (not Copy Shipment): attempt to re-attach a copied stop to a
		// saved address book entry it exactly matches, so the "Saved Address"
		// search box shows the company name instead of just the postal code.
		// Skipped for Copy Shipment to match the existing-quote reload behavior
		// (see resolveSide below), which intentionally keeps copied stops
		// detached there.
		if (!isCopy) {
			const recallKey = location.key;

			const resolveCopiedSide = async (
				side: "pickup" | "delivery",
				stopDetails: typeof quoteRecall.pickupDetails,
				stopLocation: LocationValue | undefined,
			) => {
				const address1 = stopDetails?.address1?.trim();
				const postalCode = stopLocation?.postalCode?.trim();
				if (!address1 || !postalCode) return;

				try {
					const items = await queryClient.fetchQuery({
						queryKey: [
							"addressBookSearch",
							postalCode,
							resolvedCustomerId,
							isInternal,
						],
						queryFn: () =>
							searchAddressBookItems({
								searchTerm: postalCode,
								customerId: resolvedCustomerId,
								isInternal,
							}),
						staleTime: 1000 * 60 * 5,
					});
					// Guard: only apply if this is still the active copy navigation
					if (appliedRecallKeyRef.current !== recallKey) return;
					const match = findExactMatchingAddressBookItem(
						items,
						stopDetails,
						stopLocation,
						side,
					);
					if (match) {
						setSelectedAddressBookItem(side, match, "auto");
					}
				} catch (error) {
					console.error(
						`Failed to resolve ${side} address book item from search:`,
						error,
					);
				}
			};

			// Internal API requires a customerId in the URL to resolve addresses.
			if (!isInternal || resolvedCustomerId) {
				void resolveCopiedSide(
					"pickup",
					quoteRecall.pickupDetails,
					body.origin as LocationValue | undefined,
				);
				void resolveCopiedSide(
					"delivery",
					quoteRecall.deliveryDetails,
					body.destination as LocationValue | undefined,
				);
			}
		}
	}, [
		quoteId,
		quoteRecall,
		copyQuoteCustomer,
		pageLoadData,
		allAccessorials,
		setQuoteState,
		setSelectedAddressBookItem,
		location.key,
		isCopy,
		isInternal,
		queryClient,
	]);

	// Customer data fetching (configurable)
	const fetchCustomerData = useCallback(
		async (customerId: number) => {
			const fullCustomerData = await getAndValidate(
				`${apiRoutes.SELECTCUSTOMER}${customerId}`,
				customerDetailsSchema,
			);

			const customerCredit = await getAndValidate(
				`${apiRoutes.CREDITINFO}${customerId}`,
				creditLimitSchema,
			);

			if (fullCustomerData?.success) {
				setCustomerDetails(fullCustomerData.data);
			} else {
				console.error(
					"Failed to fetch customer details:",
					fullCustomerData?.error ?? fullCustomerData,
				);
			}

			if (customerCredit?.success) {
				setCreditDetails({ customerCredits: customerCredit.data });
			} else {
				console.error(
					"Failed to fetch customer credit details:",
					customerCredit?.error ?? customerCredit,
				);
			}
		},
		[setCustomerDetails, setCreditDetails],
	);

	useEffect(() => {
		if (!includeCustomerData) return;
		if (!quoteState.customer?.id) return;

		const timeoutId = setTimeout(() => {
			void fetchCustomerData(quoteState.customer.id);
		}, 100); // Debounce by 100ms

		return () => clearTimeout(timeoutId);
	}, [includeCustomerData, quoteState.customer?.id, fetchCustomerData]);

	return {
		shouldFetchQuote: shouldLoadQuote,
		isLoadingQuote,
		quoteIsFetched,
		quoteState, // Return quoteState for easier access
		loadedQuoteId,
		// True for a Copy Quote/Shipment navigation — used by consumers to skip
		// applying user defaults (saved address/commodity/template) that would
		// otherwise overwrite the copied shipment's data.
		isCopyNavigation: isCopy || Boolean(location.state?.isCopyQuote),
	};
};

export const usePageDataLoader = <T>({
	pageDataQuery,
	quoteLoader,
	enableCondition,
}: {
	pageDataQuery: (
		customerId: number,
		options: { enabled: boolean },
	) => QueryObserverResult<T>;
	quoteLoader: ReturnType<typeof useQuoteLoader>;
	enableCondition?: () => boolean;
}) => {
	const [triggerPageQueries, setTriggerPageQueries] = useState(false);
	const { quoteState } = useContext(QuoteDetailsContext);

	const customerId = quoteState.customer?.id ?? 0;
	const shouldRunPageQueries =
		!quoteLoader.shouldFetchQuote || quoteLoader.quoteIsFetched;

	useEffect(() => {
		if (shouldRunPageQueries && customerId > 0) {
			setTriggerPageQueries(true);
		}
	}, [shouldRunPageQueries, customerId]);

	const queryResult = pageDataQuery(customerId, {
		enabled: triggerPageQueries && (enableCondition?.() ?? true),
	});

	const { isLoading, data, refetch } = queryResult;
	useEffect(() => {
		if (
			shouldRunPageQueries &&
			customerId > 0 &&
			!isLoading &&
			!data &&
			refetch
		) {
			refetch();
		}
	}, [shouldRunPageQueries, customerId, isLoading, data, refetch]);

	return {
		...queryResult,
		triggerPageQueries,
		shouldRunPageQueries,
	};
};

export const useErrorHandler = () => {
	const [showErrorModal, setShowErrorModal] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");

	const handleError = useCallback((message: string) => {
		setErrorMessage(message);
		setShowErrorModal(true);
	}, []);

	const closeErrorModal = useCallback(() => {
		setShowErrorModal(false);
	}, []);

	return {
		showErrorModal,
		errorMessage,
		handleError,
		closeErrorModal,
	};
};

export const useFormHandlers = () => {
	const { onChange, quoteState } = useContext(QuoteDetailsContext);

	const getChangeHandler = useCallback(
		(field: keyof QuoteStateType) =>
			({ value }: { value: OnChange }) => {
				// DatePicker passes a Date object; convert to ISO string for shipmentDate
				if (field === "shipmentDate" && value instanceof Date) {
					onChange(field, formatDateToLocalISOString(value));
				} else {
					onChange(field, value);
				}
			},
		[onChange],
	);

	const formInitialValues = useMemo(
		() => ({
			...quoteState,
			shipmentDate: new Date(quoteState.shipmentDate),
		}),
		[quoteState],
	);

	return {
		getChangeHandler,
		formInitialValues,
	};
};

// Generic modal management hook that can be reused across components
export const useModalManager = <T extends Record<string, boolean>>(
	initialState: T,
) => {
	const [showModal, setShowModal] = useState(initialState);

	const toggleModal = useCallback(
		(modalName: keyof T, shouldShow?: boolean) => {
			setShowModal((prevState) => ({
				...prevState,
				[modalName]: shouldShow ?? !prevState[modalName],
			}));
		},
		[],
	);

	const openModal = useCallback(
		(modalName: keyof T) => {
			toggleModal(modalName, true);
		},
		[toggleModal],
	);

	const closeModal = useCallback(
		(modalName: keyof T) => {
			toggleModal(modalName, false);
		},
		[toggleModal],
	);

	return {
		showModal,
		toggleModal,
		openModal,
		closeModal,
	};
};

// Hook for managing loading/saving states
export const useLoadingState = (initialValue = false) => {
	const [isLoading, setIsLoading] = useState(initialValue);

	const startLoading = useCallback(() => setIsLoading(true), []);
	const stopLoading = useCallback(() => setIsLoading(false), []);

	const withLoading = useCallback(
		async <T>(asyncFn: () => Promise<T>): Promise<T> => {
			startLoading();
			try {
				const result = await asyncFn();
				return result;
			} finally {
				stopLoading();
			}
		},
		[startLoading, stopLoading],
	);

	return {
		isLoading,
		setIsLoading,
		startLoading,
		stopLoading,
		withLoading,
	};
};
